// Simple SQL validation utility
// This is a basic validator. For more advanced validation, consider integrating a SQL parser library.

// Forbidden SQL keywords (case-insensitive, word boundaries)
const forbidden =
  /\b(INSERT|UPDATE|DELETE|DROP|ALTER|TRUNCATE|CREATE|REPLACE|GRANT|REVOKE|MERGE|CALL|EXEC|EXECUTE|DESCRIBE|SHOW|USE|SET|PRAGMA|ATTACH|DETACH|BEGIN|COMMIT|ROLLBACK)\b/i;

/**
 * Validates a SQL query string to ensure it is safe and follows basic rules.
 * - Must not be empty
 * - Must not contain multiple statements (no semicolons except at the end)
 * - Must not contain comments
 * - Must not contain forbidden statements (e.g., DDL/DML)
 * - Must start with SELECT
 */
export function validateSQLQuery(query: string): string | null {
  if (!query || !query.trim()) {
    return 'Query cannot be empty.';
  }
  // Disallow multiple statements (no semicolons except at the end, and not required)
  const semicolonMatch = query.match(/;/g);
  if (semicolonMatch && semicolonMatch.length > 1) {
    return 'Only a single SQL statement is allowed.';
  }
  if (/;\s*[^\s]*\S/.test(query)) {
    return 'Only a single SQL statement is allowed.';
  }
  // Disallow comments (to prevent SQL injection via comments)
  if (/--|\/\*/.test(query)) {
    return 'SQL comments are not allowed.';
  }
  // Basic check for forbidden statements (e.g., DDL/DML)
  if (forbidden.test(query)) {
    return 'Only safe SELECT queries are allowed.';
  }

  // Statements must start with SELECT or WITH
  const trimmedQuery = query.trim();
  if (!/^SELECT\b/i.test(trimmedQuery) && !/^WITH\b/i.test(trimmedQuery)) {
    return 'Queries must start with SELECT or WITH.';
  }

  return null; // No error
}

/*
Generate Metric Retention SQL Query
*/
export const generateMetricRetentionQuery = (
  idField: any,
  projectId: string,
) => {
  // Escape single quotes in projectId to prevent SQL injection
  const escapedProjectId = projectId.replace(/'/g, "''");

  const query = `
  WITH
    total_users AS (
        SELECT count() AS total_count FROM (
            SELECT trim(${idField}) AS ${idField}
            FROM events
            WHERE trim(${idField}) != '' AND project_id = '${escapedProjectId}'
            GROUP BY ${idField}
        )
    ),

    first_seen AS (
        SELECT
            trim(${idField}) AS ${idField},
            min(toDate(timestamp)) AS first_date
        FROM events
        WHERE trim(${idField}) != '' AND project_id = '${escapedProjectId}'
        GROUP BY ${idField}
    )

SELECT
    round(
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 7 , f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100, 1
    ) AS day_7_retention,
    round(
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 30, f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100, 1
    ) AS day_30_retention,
    round(
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 90, f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100, 1
    ) AS day_90_retention,
    round(
      (
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 7 , f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100
      ) -
      (
        SELECT
          count(DISTINCT if(
            toDate(e.timestamp) >= f.first_date + 7
            AND toDate(e.timestamp) <= today() - 7,
            f.${idField}, NULL
          )) / (SELECT count() FROM (
                  SELECT trim(${idField}) AS ${idField}
                  FROM events
                  WHERE trim(${idField}) != '' AND project_id = '${escapedProjectId}'
                  GROUP BY ${idField}
                  HAVING min(toDate(timestamp)) <= today() - 14
                )) * 100
        FROM first_seen f
        JOIN events e ON trim(e.${idField}) = f.${idField}
        WHERE f.first_date <= today() - 14
      )
      , 1
    ) AS day_7_delta,
    round(
      (
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 30, f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100
      ) -
      (
        SELECT
          count(DISTINCT if(
            toDate(e.timestamp) >= f.first_date + 30
            AND toDate(e.timestamp) <= today() - 7,
            f.${idField}, NULL
          )) / (SELECT count() FROM (
                  SELECT trim(${idField}) AS ${idField}
                  FROM events
                  WHERE trim(${idField}) != '' AND project_id = '${escapedProjectId}'
                  GROUP BY ${idField}
                  HAVING min(toDate(timestamp)) <= today() - 37
                )) * 100
        FROM first_seen f
        JOIN events e ON trim(e.${idField}) = f.${idField}
        WHERE f.first_date <= today() - 37
      )
      , 1
    ) AS day_30_delta,
    round(
      (
        count(DISTINCT if(toDate(e.timestamp) >= f.first_date + 90, f.${idField}, NULL))
        / (SELECT total_count FROM total_users) * 100
      ) -
      (
        SELECT
          count(DISTINCT if(
            toDate(e.timestamp) >= f.first_date + 90
            AND toDate(e.timestamp) <= today() - 7,
            f.${idField}, NULL
          )) / (SELECT count() FROM (
                  SELECT trim(${idField}) AS ${idField}
                  FROM events
                  WHERE trim(${idField}) != '' AND project_id = '${escapedProjectId}'
                  GROUP BY ${idField}
                  HAVING min(toDate(timestamp)) <= today() - 97
                )) * 100
        FROM first_seen f
        JOIN events e ON trim(e.${idField}) = f.${idField}
        WHERE f.first_date <= today() - 97
      )
      , 1
    ) AS day_90_delta
FROM first_seen f
JOIN events e ON trim(e.${idField}) = f.${idField}
  `;

  return query;
};
